import { showError } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { SuiviWalterForm } from "..";
import {
  DashboardAbsenceDto,
  DashboardAbsenceDtoFromJSON,
  DashboardApi,
  EContentHidingMode,
  EFormePersonne,
  ETypeDernierAvertissement,
  FcbSuiviApprenantDetailsDto,
  SuiviApprenantApi
} from "../../../../../api";
import { useAuth, useDialog } from "../../../../../contexts";
import { useApiService, useCrudApi, useTabMessage, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { exportFile } from "../../../../../utils";
import { useLocation } from "react-router-dom";
import { ERoutes } from "../../../../../AppRouter";
import { useReferential } from "../../../../../hooks/useReferential";

export interface ISuiviApprenantWalterDetailPageProps {
  baseRoute: string;
}

export const SuiviApprenantWalterDetailPage: React.FunctionComponent<ISuiviApprenantWalterDetailPageProps> = ({
  baseRoute
}) => {
  const { t } = useTl();
  const timeZone = "Europe/Paris";
  const api = useApiService(SuiviApprenantApi);
  const dashboardApi = useApiService(DashboardApi);
  const { id, tab, idSuivi, state } = useParams<{ id: string; tab: string; idSuivi: string; state: string }>();
  const editMode = React.useMemo(() => state === "edit", [state]);
  const idApprenant = React.useMemo(() => +id, [id]);
  const IdSuivi = React.useMemo(() => +idSuivi, [idSuivi]);
  const { user } = useAuth();
  const history = useHistory();
  const { search } = useLocation();
  const cfs = React.useMemo(() => new URLSearchParams(search).get("cfs"), [search]);
  const { messageValue, clearMessageValue, unreadyTab } = useTabMessage(cfs);
  const [absencesRow, setAbsencesRow] = React.useState<DashboardAbsenceDto>();
  React.useEffect(() => {
    if (!!messageValue) {
      setAbsencesRow(messageValue);
      clearMessageValue();
    }
  }, [clearMessageValue, messageValue]);

  const [, , , modes] = useReferential(a => a.referentialGetModes(), true, []);

  const { data, loading, deleteItem, deleting, saveItem, saving, validationErrors, refresh } = useCrudApi(React.useMemo(
      () => ({
        getApiFn: async () => {
          const suivi = await api.suiviApprenantGet({
            id: IdSuivi,
            idapprenant: absencesRow?.idapprenant ?? +id,
            typeAvertissement: absencesRow?.typeAvertissement
          });
          if (IdSuivi <= 0) {
            if (!!absencesRow) {
              // const dateUtc = parseISO(absencesRow.dateSuivi);
              // suivi.dateSuivi = utcToZonedTime(dateUtc, timeZone);
              suivi.contenu = absencesRow.datesAbsences
                ? `Dates d'absences injustifiées : ${absencesRow.datesAbsences}`
                : null;
            } else {
              suivi.idsuivi = 0;
              suivi.idrealisateurSuivi = user.iduser;
              suivi.encodeur = user.nom + " " + user.prenom;
              suivi.dateSuivi = new Date();
              suivi.contentHidingMode = EContentHidingMode.NONE;
            }
          }

          return suivi;
        },
        closeCfs: false,
        saveApiFn: d => api.suiviApprenantSave({ FcbSuiviApprenantDetailsDto: d }),
        onSaved:
          !!absencesRow &&
          (async (d: FcbSuiviApprenantDetailsDto) => {
            const mode = modes.find(m => +m.idValue === d.idmode)?.keyValue;
            if (mode === "MAIL" && absencesRow.typeAvertissement === ETypeDernierAvertissement.Convocation)
              absencesRow.typeAvertissement = ETypeDernierAvertissement.Avertissement;

            if (mode === "COURRIER" && absencesRow.typeAvertissement === ETypeDernierAvertissement.Avertissement)
              absencesRow.typeAvertissement = ETypeDernierAvertissement.Convocation;

            var res = await dashboardApi.dashboardMarquerAbsenceVue({
              DashboardAbsenceDto: DashboardAbsenceDtoFromJSON(absencesRow)
            });
            if (absencesRow.typeAvertissement === ETypeDernierAvertissement.Avertissement) {
              window.open(`/#${ERoutes.report}/absencedetailmainjp.rpt`, "_blank");
              const el = document.createElement("a");
              el.setAttribute("href", `mailto:${res.emailIfapme}?cc=${res.emailPerso}`);
              el.click();
              el.remove();
            } else {
              window.open(`/#${ERoutes.report}/absencemainjp_v2_0_0.rpt`);
              setTimeout(() => window.open(`/#${ERoutes.report}/absencedetailmainjp.rpt`), 500);
            }
            unreadyTab();
            history.push(`${ERoutes.apprenant}/${id}/suiviApprenant/${d.idsuivi}/${state}/walter`);
          }),
        onSavedRoute:
          !absencesRow &&
          ((d: FcbSuiviApprenantDetailsDto) => {
            clearMessageValue();
            return `${ERoutes.apprenant}/${id}/suiviApprenant/${d.idsuivi}/${state}/walter`;
          }),
        getDeps: [IdSuivi, absencesRow],
        deleteApiFn: (d: FcbSuiviApprenantDetailsDto) => api.suiviApprenantDelete({ id: d.idsuivi }),
        onDeletedRoute: d => `${baseRoute}/${idApprenant}/${tab}/${state}`
      }),
      [
        unreadyTab,
        absencesRow,
        IdSuivi,
        api,
        id,
        user.iduser,
        user.nom,
        user.prenom,
        modes,
        dashboardApi,
        history,
        baseRoute,
        idApprenant,
        tab,
        state,
        clearMessageValue
      ]
    )
  );

  const onCancel = React.useCallback(() => history.push(`${baseRoute}/${id}/${tab}/${state}`), [
    baseRoute,
    history,
    id,
    state,
    tab
  ]);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idsuiviFichier);
          await api.suiviApprenantDeleteSuiviApprenantFichier({
            id: idsuiviFichier
          });
          refresh();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, refresh, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      try {
        setRowLoading(idsuiviFichier);
        const file = await api.suiviApprenantDownloadSuiviApprenantFichier({
          id: idsuiviFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  return (
    <SuiviWalterForm
      data={data}
      suiviId={+IdSuivi}
      saveItem={saveItem}
      loading={loading}
      saving={saving}
      editMode={editMode && !data?.fromSmile}
      deleteItem={deleteItem}
      deleting={deleting}
      onCancel={onCancel}
      validationErrors={validationErrors}
      fieldsetTitle={t(ETLCodes.Suivi)}
      deleteFichier={deleteFichier}
      downloadFichier={downloadFichier}
      rowLoading={rowLoading}
      refreshData={refresh}
      forme={EFormePersonne.Apprenant}
    />
  );
};
